import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useCategories } from "@/components/landing/useProducts";
import { ImageUpload } from "./ImageUpload";
import { OptionsEditor, ColorsEditor, field, label } from "./ProductEditors";
import { RelatedPicker } from "./RelatedPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const toDollars = (c) => (c == null ? "" : (c / 100).toString());
const toCents = (v) => (v === "" || v == null ? null : Math.round(parseFloat(v) * 100));

const blank = {
  slug: "", name: "", category: "hoodie-sets", type: "", spec: "", description: "", edition: "", badge: "",
  featured: false, earlyBeliever: false, active: true, stock: "", image: "", gallery: [], position: "object-top", colors: [], sizeChart: [], related: [],
  options: [{ id: "set", label: "Full Set", sub: "", price: "", regular: "", sized: true }],
};

const fromProduct = (p) => ({
  ...blank, ...p,
  stock: p.stock ?? "",
  options: p.options.map((o) => ({ ...o, locked: true, price: toDollars(o.price), regular: toDollars(o.regular), cost: toDollars(o.cost) })),
});

export const ProductForm = ({ product, headers, onClose, onSaved, allProducts = [] }) => {
  const CATEGORIES = useCategories();
  const [f, setF] = useState(product ? fromProduct(product) : blank);
  const [saving, setSaving] = useState(false);
  const set = (patch) => setF((s) => ({ ...s, ...patch }));
  const toggleChart = (k) => set({ sizeChart: f.sizeChart.includes(k) ? f.sizeChart.filter((x) => x !== k) : [...f.sizeChart, k] });

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...f,
      slug: f.slug || f.name,
      stock: f.stock === "" || f.stock == null ? null : parseInt(f.stock, 10),
      options: f.options.map(({ locked, ...o }) => ({ ...o, price: toCents(o.price), regular: toCents(o.regular) || null, cost: toCents(o.cost), lookupKey: locked ? o.lookupKey : undefined })),
    };
    if (payload.options.some((o) => !o.label || !o.price)) return toast.error("Every option needs a label and a price.");
    if (!payload.image) return toast.error("Add a cover image.");
    setSaving(true);
    try {
      const r = product
        ? await axios.put(`${API}/admin/products/${product.id}`, payload, { headers })
        : await axios.post(`${API}/admin/products`, payload, { headers });
      toast.success(product ? "Product updated" : "Product added");
      onSaved(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save product.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="product-form" data-lenis-prevent className="max-w-3xl w-[calc(100%-2rem)] max-h-[92vh] overflow-y-auto overscroll-contain bg-[#faf9f6] border-[#e3e2dc] text-[#0a0a0b] rounded-2xl p-6 sm:p-8">
        <DialogTitle className="font-display font-extrabold uppercase text-2xl">{product ? "Edit product" : "New product"}</DialogTitle>
        <form onSubmit={save} className="mt-4 space-y-6">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><span className={label}>Name</span>
              <input data-testid="pf-name" className={field} required value={f.name} onChange={(e) => set({ name: e.target.value })} placeholder="Crescent & Crown Edition" /></div>
            <div><span className={label}>Category</span>
              <select data-testid="pf-category" className={field} value={f.category} onChange={(e) => set({ category: e.target.value })}>
                {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select></div>
            <div><span className={label}>Badge (optional)</span>
              <div className="flex gap-2">
                <input data-testid="pf-badge" className={field} value={f.badge} onChange={(e) => set({ badge: e.target.value })} placeholder="Drop 001 · No. 1" />
                {f.category === "palestine" && (
                  <button type="button" data-testid="pf-special-edition" onClick={() => set({ badge: "Special Edition" })} className={`shrink-0 h-10 px-4 rounded-full font-mono-d text-[9px] uppercase tracking-[0.2em] transition-colors ${f.badge === "Special Edition" ? "bg-[#007a3d] text-white" : "border border-[#007a3d] text-[#007a3d] hover:bg-[#007a3d] hover:text-white"}`}>
                    Special Edition
                  </button>
                )}
              </div></div>
            <div><span className={label}>Availability line</span>
              <input data-testid="pf-edition" className={field} value={f.edition} onChange={(e) => set({ edition: e.target.value })} placeholder="Only 300 available" /></div>
            <div><span className={label}>Stock (units · powers the live "X of Y left" counter; blank = unlimited)</span>
              <input data-testid="pf-stock" className={field} type="number" min="0" value={f.stock} onChange={(e) => set({ stock: e.target.value })} placeholder="300" /></div>
            <div className="sm:col-span-2"><span className={label}>Description</span>
              <textarea data-testid="pf-description" className={`${field} h-24 py-2`} value={f.description} onChange={(e) => set({ description: e.target.value })} /></div>
          </div>

          <div className="flex flex-wrap gap-5">
            {[["featured", "Featured / preferred"], ["earlyBeliever", "Early Believer pricing"], ["active", "Visible on site"]].map(([k, l]) => (
              <label key={k} className="flex items-center gap-2 font-mono-d text-[10px] uppercase tracking-[0.2em] cursor-pointer">
                <input data-testid={`pf-${k}`} type="checkbox" checked={f[k]} onChange={(e) => set({ [k]: e.target.checked })} className="accent-[#ff2a85] h-4 w-4" /> {l}
              </label>
            ))}
            <span className="flex items-center gap-3 font-mono-d text-[10px] uppercase tracking-[0.2em]">
              Size chart:
              {["hoodie", "pants"].map((k) => (
                <label key={k} className="flex items-center gap-1.5 cursor-pointer">
                  <input data-testid={`pf-chart-${k}`} type="checkbox" checked={f.sizeChart.includes(k)} onChange={() => toggleChart(k)} className="accent-[#ff2a85] h-4 w-4" /> {k}
                </label>
              ))}
            </span>
          </div>

          <div><span className={label}>Cover photo (shown on the card)</span>
            <ImageUpload headers={headers} images={f.image} onChange={(image) => set({ image })} testId="pf-cover" /></div>
          <div><span className={label}>More photos (gallery · shown in the quick view, add as many as you like)</span>
            <ImageUpload headers={headers} images={f.gallery || []} multiple onChange={(gallery) => set({ gallery })} testId="pf-gallery" /></div>

          <OptionsEditor options={f.options} onChange={(options) => set({ options })} />
          <ColorsEditor colors={f.colors} onChange={(colors) => set({ colors })} headers={headers} />
          <RelatedPicker value={f.related || []} onChange={(related) => set({ related })} currentId={product?.id} products={allProducts} />

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" data-testid="pf-cancel" onClick={onClose} className="h-11 px-5 rounded-full border border-[#e3e2dc] font-mono-d text-[10px] uppercase tracking-[0.2em] hover:border-[#0a0a0b]">Cancel</button>
            <button type="submit" data-testid="pf-save" disabled={saving} className="h-11 px-6 rounded-full bg-[#ff2a85] hover:bg-[#e60067] text-white font-mono-d text-[10px] uppercase tracking-[0.2em] disabled:opacity-60 flex items-center gap-2">
              {saving && <Loader2 size={14} className="animate-spin" />} {product ? "Save changes" : "Add product"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
